import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGame } from "@/context/GameContext";
import { toast } from "sonner";

const BG_URL  = "/tournament-bg.jpg";
const GOLD    = "#F1E194";
const GOLD_DIM = "rgba(241,225,148,0.35)";
const CONN    = "rgba(241,225,148,0.18)";
const CONN_DONE = "rgba(241,225,148,0.5)";

// ── Same background component as Setup page ───────────────────────────────────
function TourBg() {
  return (
    <>
      <div style={{
        position: "fixed", inset: 0, zIndex: 0,
        backgroundImage: `url('${BG_URL}')`,
        backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat",
      }} />
      <div style={{
        position: "fixed", inset: 0, zIndex: 1,
        background: "linear-gradient(180deg, rgba(18,3,7,0.58) 0%, rgba(8,1,3,0.65) 100%)",
      }} />
    </>
  );
}

// ── Fireworks ─────────────────────────────────────────────────────────────────
function startFireworks(canvas) {
  if (!canvas) return () => {};
  canvas.width = canvas.offsetWidth || window.innerWidth;
  canvas.height = canvas.offsetHeight || window.innerHeight;
  const ctx = canvas.getContext("2d");
  const particles = [];
  const COLS = ["#FFD700","#F1E194","#FFA500","#FF6B6B","#4ECDC4","#fff","#ff9500","#ffe066","#c084fc","#f9a8d4"];
  class P {
    constructor(x, y) {
      this.x = x; this.y = y;
      const a = Math.random() * Math.PI * 2, s = Math.random() * 7 + 2;
      this.vx = Math.cos(a)*s; this.vy = Math.sin(a)*s-4;
      this.alpha=1; this.color=COLS[Math.floor(Math.random()*COLS.length)];
      this.size=Math.random()*5+2; this.gravity=0.13;
    }
    update() { this.x+=this.vx; this.y+=this.vy; this.vy+=this.gravity; this.alpha-=0.016; }
    draw() { ctx.save(); ctx.globalAlpha=Math.max(0,this.alpha); ctx.fillStyle=this.color; ctx.beginPath(); ctx.arc(this.x,this.y,this.size,0,Math.PI*2); ctx.fill(); ctx.restore(); }
  }
  let lastLaunch=0, raf;
  const launch = () => { const x=Math.random()*canvas.width, y=Math.random()*canvas.height*0.55; for(let i=0;i<90;i++) particles.push(new P(x,y)); };
  const frame = t => {
    ctx.clearRect(0,0,canvas.width,canvas.height);
    if(t-lastLaunch>450){launch(); if(Math.random()>.5)launch(); lastLaunch=t;}
    for(let i=particles.length-1;i>=0;i--){ particles[i].update(); if(particles[i].alpha<=0){particles.splice(i,1);continue;} particles[i].draw(); }
    raf=requestAnimationFrame(frame);
  };
  raf=requestAnimationFrame(frame);
  return () => cancelAnimationFrame(raf);
}

// ── Team avatar ────────────────────────────────────────────────────────────────
function TeamAvatar({ team, size=36 }) {
  if (!team) return <div style={{ width:size, height:size, borderRadius:"50%", background:"rgba(255,255,255,0.08)", flexShrink:0 }} />;
  if (team.image) return <img src={team.image} alt={team.name} style={{ width:size, height:size, borderRadius:"50%", objectFit:"cover", border:`2px solid ${team.color}77`, flexShrink:0 }} />;
  return (
    <div style={{
      width:size, height:size, borderRadius:"50%", flexShrink:0,
      background:`linear-gradient(135deg, ${team.color}20, ${team.color}55)`,
      border:`2px solid ${team.color}44`,
      display:"flex", alignItems:"center", justifyContent:"center",
      fontSize:size*.45, color:team.color,
    }}>🏆</div>
  );
}

// ── Champion celebration ───────────────────────────────────────────────────────
function ChampionScreen({ champTeam, teams, rounds, onNewTournament, onHome }) {
  const canvasRef = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 200);
    const stop = startFireworks(canvasRef.current);
    return () => { stop(); clearTimeout(t); };
  }, []);
  const wins = rounds.filter(r => r.matches.some(m => m.winner === champTeam.id && !m.isBye)).length;
  const totalRealMatches = rounds.reduce((a,r) => a+r.matches.filter(m=>!m.isBye).length, 0);

  return (
    <div style={{ position:"fixed", inset:0, zIndex:200, overflow:"hidden", background:"radial-gradient(ellipse at 50% 30%, #1a0800 0%, #0d0500 50%, #000 100%)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
      <style>{`
        @keyframes tDrop { 0%{opacity:0;transform:translateY(-100px) scale(.5) rotate(-12deg);} 65%{opacity:1;transform:translateY(8px) scale(1.08) rotate(2deg);} 100%{opacity:1;transform:none;} }
        @keyframes cPulse { 0%,100%{text-shadow:0 0 30px currentColor,0 0 60px currentColor;} 50%{text-shadow:0 0 60px currentColor,0 0 120px currentColor;} }
        @keyframes sIn { from{opacity:0;transform:scale(.85);} to{opacity:1;transform:scale(1);} }
        @keyframes ls { 0%,100%{transform:rotate(-3deg);} 50%{transform:rotate(3deg);} }
      `}</style>
      <canvas ref={canvasRef} style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none", zIndex:1 }} />
      <div style={{ position:"relative", zIndex:5, textAlign:"center", padding:"0 max(40px,8vw)", maxWidth:620, width:"100%" }}>
        <div style={{ fontSize:"clamp(4.5rem,12vw,8rem)", lineHeight:1, marginBottom:10, animation:show?"tDrop 1.1s cubic-bezier(.34,1.56,.64,1) forwards":"none", opacity:show?undefined:0, filter:"drop-shadow(0 0 32px rgba(255,200,0,.8))" }}>🏆</div>
        <div style={{ fontFamily:"Cairo,sans-serif", fontSize:"clamp(1.5rem,4.5vw,2.6rem)", fontWeight:900, color:GOLD, marginBottom:6, letterSpacing:".12em" }}>حُجّة</div>
        <div style={{ color:"rgba(241,225,148,.4)", fontSize:".8rem", fontWeight:700, letterSpacing:".3em", marginBottom:14, textTransform:"uppercase" }}>👑 Champion 👑</div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"clamp(8px,2vw,18px)", marginBottom:18 }}>
          <span style={{ fontSize:"clamp(1.3rem,3vw,2rem)", animation:"ls 3s ease-in-out infinite" }}>🌿</span>
          <span style={{ color:champTeam.color, fontFamily:"Cairo,sans-serif", fontWeight:900, fontSize:"clamp(1.7rem,5.5vw,3.5rem)", animation:"cPulse 2s ease-in-out infinite", wordBreak:"break-word", maxWidth:"55vw" }}>{champTeam.name}</span>
          <span style={{ fontSize:"clamp(1.3rem,3vw,2rem)", transform:"scaleX(-1)", display:"inline-block", animation:"ls 3s ease-in-out 1.5s infinite" }}>🌿</span>
        </div>
        {champTeam.image && <div style={{ display:"flex", justifyContent:"center", marginBottom:18 }}><img src={champTeam.image} alt={champTeam.name} style={{ width:"clamp(64px,10vw,90px)", height:"clamp(64px,10vw,90px)", borderRadius:"50%", objectFit:"cover", border:`3px solid ${champTeam.color}`, boxShadow:`0 0 32px ${champTeam.color}66`, animation:"sIn .8s ease-out .5s both" }} /></div>}
        <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap", marginBottom:24, animation:"sIn .8s ease-out .7s both" }}>
          {[{label:"انتصارات",v:wins,e:"⚔️"},{label:"مباريات",v:totalRealMatches,e:"🎮"}].map((s,i)=>(
            <div key={i} style={{ background:"rgba(241,225,148,.06)", border:"1px solid rgba(241,225,148,.18)", borderRadius:14, padding:"10px 18px", textAlign:"center" }}>
              <div style={{ fontSize:"1.3rem" }}>{s.e}</div>
              <div style={{ color:GOLD, fontWeight:900, fontSize:"1.4rem", fontFamily:"Cairo" }}>{s.v}</div>
              <div style={{ color:"rgba(241,225,148,.35)", fontSize:".72rem", fontWeight:700 }}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap", animation:"sIn .8s ease-out 1s both" }}>
          <button onClick={onNewTournament} style={{ background:"linear-gradient(135deg,#5B0E14,#9A1E28)", color:GOLD, border:"2px solid rgba(241,225,148,.35)", borderRadius:28, padding:"13px 28px", fontWeight:900, fontSize:".95rem", fontFamily:"Cairo", cursor:"pointer" }}>🏆 بطولة جديدة</button>
          <button onClick={onHome} style={{ background:"rgba(255,255,255,.06)", color:"rgba(241,225,148,.6)", border:"1.5px solid rgba(241,225,148,.18)", borderRadius:28, padding:"13px 24px", fontWeight:700, fontSize:".95rem", fontFamily:"Cairo", cursor:"pointer" }}>الرئيسية</button>
        </div>
      </div>
    </div>
  );
}

// ── Match card ─────────────────────────────────────────────────────────────────
function MatchCard({ match, teams, isActive, roundIdx, matchIdx, onRecordWinner, onPlay }) {
  const t1=match.team1, t2=match.team2;
  const winner = match.winner ? teams.find(t=>t.id===match.winner) : null;
  const done = !!match.winner;

  if (match.isBye) {
    const t=t1||t2;
    return (
      <div style={{ background:`${t?.color}0e`, border:`1px solid ${t?.color}25`, borderRadius:12, padding:"10px 14px", textAlign:"center", minWidth:240 }}>
        <div style={{ color:t?.color, fontWeight:900, fontSize:14, fontFamily:"Cairo" }}>{t?.name}</div>
        <div style={{ color:"rgba(255,255,255,.2)", fontSize:11, marginTop:2 }}>تعبئة تلقائية ✓</div>
      </div>
    );
  }

  // active: subtle gold border + tiny shadow — NOT a neon aura
  const borderCol = isActive ? "rgba(241,225,148,0.6)" : done ? "rgba(34,197,94,0.28)" : "rgba(255,255,255,0.08)";
  const shadow = isActive
    ? "0 0 0 1px rgba(241,225,148,0.15), 0 4px 20px rgba(91,14,20,0.5)"
    : done ? "0 2px 12px rgba(34,197,94,0.08)" : "0 2px 12px rgba(0,0,0,0.45)";

  return (
    <div style={{
      width:280, borderRadius:14, overflow:"hidden",
      background: done ? "rgba(6,2,10,0.88)" : isActive ? "rgba(60,5,18,0.78)" : "rgba(10,2,6,0.75)",
      border:`2px solid ${borderCol}`,
      boxShadow: shadow,
      transition:"all 0.35s ease",
    }}>
      {[t1,t2].map((team,ti)=>{
        const isW=done&&winner?.id===team?.id;
        const isL=done&&winner&&!isW;
        return (
          <div key={ti} style={{
            display:"flex", alignItems:"center", gap:10, padding:"11px 13px",
            background:isW?`${team?.color}14`:"transparent",
            borderBottom:ti===0?"1px solid rgba(255,255,255,0.06)":"none",
          }}>
            <TeamAvatar team={team} size={32} />
            <span style={{
              flex:1, fontWeight:900, fontSize:14, fontFamily:"Cairo",
              color:team?(isL?`${team.color}2a`:team.color):"rgba(255,255,255,.15)",
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
            }}>{team?team.name:"TBD"}</span>
            {isW&&<span style={{ color:"#4ade80", fontWeight:900, fontSize:13, flexShrink:0 }}>✓</span>}
          </div>
        );
      })}

      {isActive&&!done&&t1&&t2&&(
        <div style={{ padding:"9px 10px", borderTop:"1px solid rgba(241,225,148,0.1)", display:"flex", flexDirection:"column", gap:7 }}>
          <button data-testid={`play-match-r${roundIdx}-m${matchIdx}`} onClick={onPlay}
            style={{ width:"100%", padding:"8px 0", borderRadius:10, background:"linear-gradient(135deg,#5B0E14,#9A1E28)", color:GOLD, border:"1.5px solid rgba(241,225,148,0.25)", fontWeight:900, fontSize:13, fontFamily:"Cairo", cursor:"pointer" }}>
            🎮 ابدأ المباراة
          </button>
          <div style={{ display:"flex", gap:6 }}>
            {[t1,t2].map(team=>(
              <button key={team.id} data-testid={`winner-${team.id}-r${roundIdx}-m${matchIdx}`} onClick={()=>onRecordWinner(team.id)}
                style={{ flex:1, padding:"6px 0", borderRadius:9, background:`${team.color}12`, border:`1.5px solid ${team.color}40`, color:team.color, fontWeight:900, fontSize:12, fontFamily:"Cairo", cursor:"pointer", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {team.name.length>7?team.name.slice(0,7)+"…":team.name} ✓
              </button>
            ))}
          </div>
        </div>
      )}

      {done&&(
        <div style={{ padding:"5px 13px", textAlign:"center", fontSize:11, fontWeight:900, fontFamily:"Cairo", borderTop:"1px solid rgba(34,197,94,0.1)", color:"rgba(74,222,128,.45)" }}>
          {winner?.name} يتقدم →
        </div>
      )}
    </div>
  );
}

// ── Round heading label ────────────────────────────────────────────────────────
function RoundLabel({ name, isComplete }) {
  const isFinal = name === "النهائي";
  const isSemi  = name === "نصف النهائي";
  const accent  = isComplete ? "#4ade80" : isFinal ? GOLD : isSemi ? "#fb923c" : GOLD_DIM;
  return (
    <div style={{
      display:"inline-flex", alignItems:"center", gap:5,
      padding:"5px 16px", borderRadius:24,
      background: isComplete?"rgba(74,222,128,.08)":isFinal?"rgba(241,225,148,.1)":"rgba(241,225,148,.05)",
      border:`1px solid ${accent}28`,
      color:accent, fontFamily:"Cairo,sans-serif", fontWeight:900,
      fontSize: isFinal?14:13,
    }}>
      {isComplete&&<span style={{fontSize:10}}>✓</span>}
      {isFinal&&!isComplete&&<span>👑</span>}
      {name}
    </div>
  );
}

// ── Vertical connector between two match slots → next match ───────────────────
function VConnector({ count, done }) {
  // count = number of matches on the LEFT column
  // Each pair of matches connects to one match on the right
  const pairs = count / 2;
  const color = done ? CONN_DONE : CONN;
  return (
    <div style={{ width:44, display:"flex", flexDirection:"column", alignSelf:"stretch", flexShrink:0 }}>
      {Array.from({ length: pairs }).map((_,pi)=>(
        <div key={pi} style={{ flex:1, display:"flex", flexDirection:"column" }}>
          {/* top half: bottom-right corner */}
          <div style={{ flex:1, borderRight:`2px solid ${color}`, borderBottom:`2px solid ${color}`, borderRadius:"0 0 8px 0", transition:"border-color .5s" }} />
          {/* bottom half: top-right corner */}
          <div style={{ flex:1, borderRight:`2px solid ${color}`, borderTop:`2px solid ${color}`, borderRadius:"0 8px 0 0", transition:"border-color .5s" }} />
        </div>
      ))}
    </div>
  );
}

// ── Round column ──────────────────────────────────────────────────────────────
function RoundColumn({ round, roundIdx, teams, maxMatches, nextMatch, onRecordWinner, onPlay }) {
  const count = round.matches.length;
  const slotsPerMatch = maxMatches / count;
  const isComplete = round.matches.every(m=>!!m.winner);

  return (
    <div style={{ width:300, display:"flex", flexDirection:"column", alignSelf:"stretch", flexShrink:0 }}>
      {/* Heading */}
      <div style={{ height:52, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <RoundLabel name={round.name} isComplete={isComplete} />
      </div>
      {/* Matches */}
      <div style={{ flex:1, display:"flex", flexDirection:"column" }}>
        {round.matches.map((match,mi)=>{
          const isActive = nextMatch?.roundIdx===roundIdx && nextMatch?.matchIdx===mi;
          const minH = Math.max(96, 360/count);
          return (
            <div key={match.id} style={{ flex:slotsPerMatch, display:"flex", alignItems:"center", justifyContent:"center", padding:"8px 6px", minHeight:minH }}>
              <MatchCard match={match} teams={teams} isActive={isActive} roundIdx={roundIdx} matchIdx={mi}
                onRecordWinner={wid=>onRecordWinner(roundIdx,mi,wid)}
                onPlay={()=>onPlay(match,roundIdx,mi)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main bracket page ─────────────────────────────────────────────────────────
export default function TournamentBracketPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { tournamentState, updateTournament, setGameMode, createSession, resetGame } = useGame();
  const [loading, setLoading] = useState(false);

  useEffect(()=>{
    const ar = location.state?.autoRecord;
    if (ar && tournamentState) {
      setTimeout(()=>{ recordWinner(ar.roundIdx,ar.matchIdx,ar.winnerId); navigate(location.pathname,{replace:true,state:{}}); },100);
    }
  },[]); // eslint-disable-line

  const { teams, rounds, champion } = tournamentState || {};
  const champTeam  = champion && teams ? teams.find(t=>t.id===champion) : null;
  const maxMatches = rounds?.[0]?.matches.length || 1;
  const completedRounds = rounds ? rounds.filter(r=>r.matches.every(m=>!!m.winner)).length : 0;
  const totalMatches    = rounds ? rounds.reduce((a,r)=>a+r.matches.filter(m=>!m.isBye).length,0) : 0;
  const doneMatches     = rounds ? rounds.reduce((a,r)=>a+r.matches.filter(m=>!!m.winner&&!m.isBye).length,0) : 0;

  // ── No tournament state ────────────────────────────────────────────────────
  if (!tournamentState) {
    return (
      <div style={{ minHeight:"100vh", position:"relative", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <TourBg />
        <div style={{ position:"relative", zIndex:2, textAlign:"center" }}>
          <div style={{ color:"rgba(241,225,148,.4)", fontSize:18, fontWeight:700, fontFamily:"Cairo", marginBottom:16 }}>لا توجد بطولة نشطة</div>
          <button onClick={()=>navigate("/tournament")} style={{ background:"linear-gradient(135deg,#5B0E14,#9A1E28)", color:GOLD, padding:"12px 32px", borderRadius:28, fontWeight:900, fontSize:16, fontFamily:"Cairo", border:"2px solid rgba(241,225,148,.3)", cursor:"pointer" }}>
            إنشاء بطولة
          </button>
        </div>
      </div>
    );
  }

  const findNextPendingMatch = () => {
    for (let ri=0;ri<rounds.length;ri++) {
      const prevDone = ri===0||rounds[ri-1].matches.every(m=>!!m.winner);
      if (!prevDone) break;
      for (let mi=0;mi<rounds[ri].matches.length;mi++) {
        const m=rounds[ri].matches[mi];
        if (!m.winner&&!m.isBye&&m.team1&&m.team2) return {roundIdx:ri,matchIdx:mi};
      }
    }
    return null;
  };

  const recordWinner = (roundIdx, matchIdx, winnerId) => {
    updateTournament(prev=>{
      const next=JSON.parse(JSON.stringify(prev));
      const match=next.rounds[roundIdx].matches[matchIdx];
      if (match.winner) return prev;
      match.winner=winnerId;
      if (roundIdx+1<next.rounds.length) {
        const slotIdx=Math.floor(matchIdx/2);
        const nextM=next.rounds[roundIdx+1].matches[slotIdx];
        const wo=next.teams.find(t=>t.id===winnerId);
        if (matchIdx%2===0) nextM.team1=wo; else nextM.team2=wo;
      } else { next.champion=winnerId; }
      return next;
    });
    const wName=teams.find(t=>t.id===winnerId)?.name;
    toast.success(`🏆 ${wName} يتقدم!`,{duration:2000});
  };

  const handlePlayMatch = async (match, roundIdx, matchIdx) => {
    if (!match.team1||!match.team2||loading) return;
    setLoading(true);
    try {
      setGameMode("tournament");
      resetGame();
      updateTournament(prev=>({...prev,currentMatchRef:{roundIdx,matchIdx,team1Id:match.team1.id,team2Id:match.team2.id}}));
      await createSession(match.team1.name, match.team2.name);
      navigate("/categories");
    } catch { toast.error("حدث خطأ، حاول مرة ثانية"); }
    finally { setLoading(false); }
  };

  const nextMatch     = findNextPendingMatch();
  const nextMatchData = nextMatch ? rounds[nextMatch.roundIdx].matches[nextMatch.matchIdx] : null;
  const nextRoundName = nextMatch ? rounds[nextMatch.roundIdx].name : null;

  return (
    <div style={{ height:"100vh", display:"flex", flexDirection:"column", overflow:"hidden", position:"relative" }}>
      <TourBg />
      <style>{`
        @keyframes matchPulse { 0%,100%{box-shadow:0 0 0 1px rgba(241,225,148,.15),0 4px 20px rgba(91,14,20,.5);} 50%{box-shadow:0 0 0 2px rgba(241,225,148,.22),0 4px 24px rgba(91,14,20,.65);} }
        @keyframes bannerIn { from{opacity:0;transform:translateY(-8px);} to{opacity:1;transform:none;} }
      `}</style>

      {champTeam && <ChampionScreen champTeam={champTeam} teams={teams} rounds={rounds} onNewTournament={()=>navigate("/tournament")} onHome={()=>navigate("/")} />}

      {/* ── Header ── */}
      <div style={{
        position:"relative", zIndex:10, flexShrink:0,
        padding:"0 20px",
        height:60,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        background:"rgba(6,1,3,0.75)",
        borderBottom:"1px solid rgba(241,225,148,0.1)",
      }}>
        {/* Back button — left side (RTL-aware: right side visually) */}
        <button
          onClick={()=>navigate("/tournament")}
          style={{
            display:"inline-flex", alignItems:"center", gap:6,
            background:"rgba(241,225,148,0.08)",
            border:"1px solid rgba(241,225,148,0.22)",
            borderRadius:10, padding:"7px 14px",
            color:"rgba(241,225,148,0.8)", fontFamily:"Cairo,sans-serif",
            fontWeight:700, fontSize:13, cursor:"pointer",
            whiteSpace:"nowrap",
          }}
        >
          ← رجوع
        </button>

        {/* Center — title + status */}
        <div style={{ textAlign:"center", flex:1, padding:"0 12px" }}>
          <div style={{ color:GOLD, fontWeight:900, fontSize:18, fontFamily:"Cairo,sans-serif", lineHeight:1.2 }}>
            🏆 جدول البطولة
          </div>
          {nextRoundName && !champion && (
            <div style={{ color:GOLD_DIM, fontSize:11, fontFamily:"Cairo,sans-serif", fontWeight:700, marginTop:2 }}>
              {nextRoundName} · {totalMatches-doneMatches} مباراة متبقية
            </div>
          )}
        </div>

        {/* New tournament button — right side */}
        <button
          onClick={()=>navigate("/tournament")}
          style={{
            display:"inline-flex", alignItems:"center", gap:6,
            background:"linear-gradient(135deg,#5B0E14,#7a1219)",
            border:"1px solid rgba(241,225,148,0.22)",
            borderRadius:10, padding:"7px 14px",
            color:GOLD, fontFamily:"Cairo,sans-serif",
            fontWeight:700, fontSize:13, cursor:"pointer",
            whiteSpace:"nowrap",
          }}
        >
          بطولة جديدة 🏆
        </button>
      </div>

      {/* Progress bar */}
      <div data-testid="bracket-progress" style={{ position:"relative", zIndex:10, flexShrink:0, height:3, background:"rgba(241,225,148,0.06)" }}>
        <div style={{ height:"100%", width:`${(completedRounds/rounds.length)*100}%`, background:"linear-gradient(90deg,#5B0E14,#F1E194)", transition:"width .7s ease" }} />
      </div>

      {/* Next match banner */}
      {nextMatchData&&!champion&&(
        <div data-testid="next-match-banner" style={{
          position:"relative", zIndex:10, flexShrink:0,
          padding:"7px 20px", textAlign:"center",
          background:"rgba(91,14,20,0.3)",
          borderBottom:"1px solid rgba(241,225,148,0.08)",
          animation:"bannerIn .35s ease-out",
        }}>
          <span style={{ fontWeight:900, fontSize:13, color:"rgba(241,225,148,0.78)", fontFamily:"Cairo" }}>
            {rounds[nextMatch.roundIdx].name} ·{" "}
            <span style={{ color:nextMatchData.team1?.color }}>{nextMatchData.team1?.name}</span>
            <span style={{ color:GOLD_DIM }}> ضد </span>
            <span style={{ color:nextMatchData.team2?.color }}>{nextMatchData.team2?.name}</span>
          </span>
        </div>
      )}

      {/* Bracket canvas — horizontal scroll */}
      <div style={{ position:"relative", zIndex:2, flex:1, overflowX:"auto", overflowY:"hidden", display:"flex", alignItems:"center", justifyContent:"center", padding:"16px 32px" }} dir="ltr">
        <div style={{ display:"flex", alignItems:"stretch", minHeight:460, height:"calc(100% - 16px)" }}>
          {rounds.map((round,ri)=>{
            const roundComplete = round.matches.every(m=>!!m.winner);
            return (
              <React.Fragment key={ri}>
                <RoundColumn round={round} roundIdx={ri} teams={teams} maxMatches={maxMatches} nextMatch={nextMatch} onRecordWinner={recordWinner} onPlay={handlePlayMatch} />
                {ri<rounds.length-1 && <VConnector count={round.matches.length} done={roundComplete} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div style={{ position:"relative", zIndex:10, flexShrink:0, padding:"8px 16px", textAlign:"center", background:"rgba(4,1,2,0.6)", borderTop:"1px solid rgba(241,225,148,0.07)" }}>
        <div style={{ color:"rgba(241,225,148,0.3)", fontSize:12, fontWeight:700, fontFamily:"Cairo" }}>
          {doneMatches} مباراة مكتملة · {totalMatches-doneMatches} متبقية
        </div>
      </div>
    </div>
  );
}
